import * as Sentry from '@sentry/react-native';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import {
  DeviceEventEmitter,
  Dimensions,
  ScrollView,
  TextInput,
  TouchableOpacity,
} from 'react-native';

import { ChatList } from '@components/Chat/ChatList.web';
import { EmptyChatList } from '@components/Chat/EmptyChatList.web';
import { NewChatModal } from '@components/Modals/NewChatModal';
import RadioButton from '@components/RadioButton/RadioButton';
import { Box, Text } from '@components/Restyle';
import ActivityIndicatorLoading from '@components/shared/ActivityIndicatorLoading';
import Button from '@components/shared/Button/Button';
import { FadeInView } from '@components/shared/FadeInView';
import Icon from '@components/shared/Icon/RestyleIcon';
import { MenuItem } from '@components/shared/Popup/PopupItem';
import SearchInput from '@components/shared/SearchInput/SearchInput';
import { ChatRowSkeleton } from '@components/shared/skeletons/ChatRowSkeleton';
import { SkeletonList } from '@components/shared/skeletons/SkeletonList';
import TooltipPopup from '@components/Tutorialization/TooltipPopup';
import ChatOverlay from '@components/Tutorialization/Web/ChatOverlay';
import {
  ChatEmptyState,
  EmptyStateType,
} from '@components/Web/Chats/ChatEmptyState.web';
import { Drawer } from '@components/Web/Drawer';
import {
  ChatDrawerScreen,
  useWebDrawer,
} from '@components/Web/Drawer/WebDrawerContext';
import {
  Chat,
  Document,
  ListChatsPaginationQuery,
  SearchableResult,
  useListChatsPaginationQuery,
  useMarkChatsReadMutation,
  useSearchLazyQuery,
  useCreateChatMutation,
} from '@graphql/generated';
import useActiveChat from '@hooks/useActiveChat';
import { useChatTutorialWeb } from '@hooks/useChatTutorialWeb';
import { useDebouncedSearch } from '@hooks/useDebouncedSearch';
import useEmoji from '@hooks/useEmoji';
import { useGetDocumentsByClientIdsQuery } from '@hooks/useGetDocumentsByClientIdsQuery';
import useMe from '@hooks/useMe';
import { useMinimumVisible } from '@hooks/useMinimumVisible';
import { useOnboardingActive } from '@hooks/useOnboardingActive';
import useSearch from '@hooks/useSearch';
import { useSelected } from '@hooks/useSelected';
import { useAppNavigation } from '@navigation/useAppNavigation';
import { Search, SearchResultType } from '@root/types';
import { ChatsProps } from '@screens/Chats/ChatsData';
import { appendChatResults } from '@screens/Global/chatSearchResults';
import ChatsSearch from '@screens/Global/ChatsSearch.web';
import theme from '@themes/theme';
import {
  clearOnboardingInvitees,
  findInviteeChats,
  getAssignedOnboardingInvitees,
  ONBOARDING_INVITEES_UPDATED,
  readOnboardingInvitees,
} from '@utils/onboardingInvitedMembers';
import { AsyncStorage } from '@utils/storage';
import { isChatSearchResult, isMessageSearchResult } from '@utils/typeGuards';

export const ChatListDrawer = (props: ChatsProps) => {
  const {
    listChats,
    loading,
    refresh,
    refreshing,
    loadMore,
    hasNextPage,
    fetchChats,
    hasError,
    hasLoadedOnce,
    resetError,
  } = props;
  const {
    chatExpanded,
    chatListOpen,
    setChatListOpen,
    setShouldChatListOpen,
    toggleChatListOpen,
    setIsBottomBannerShow,
    screen: currentScreen,
  } = useWebDrawer();
  const navigate = useAppNavigation();
  const { t } = useTranslation();
  const {
    setActiveMessageId,
    isOpenChat,
    setIsOpenChat,
    isNewChat,
    setIsNewChat,
  } = useActiveChat();
  const [selected, toggleSelected, selectAll, unSelectAll] =
    useSelected<(typeof listChats)[number]>();
  const [isEditing, setIsEditing] = useState(false);
  const [isArchiveList, setIsArchiveList] = useState(false);
  const [isNewChatOpen, setIsNewChatOpen] = useState(false);
  const [isQRshow, setIsQRshow] = useState(true);
  const { showMessageEmojiModal, setShowMessageEmojiModal } = useEmoji();

  const {
    isVisibleForChat,
    setShowOverlayForChat,
    isFromChatList,
    chatHeaderHeight,
    tooltipPopupHeight,
    chatTutorialWebState,
    setChatTutorialWebStateAndInStorage,
  } = useChatTutorialWeb();

  const [markChatsRead, { data: markChatsReadData }] =
    useMarkChatsReadMutation();

  // TA-10279: first-load skeleton — only the true first fetch (before any
  // chat has ever loaded), never a background refetch of an already-warm
  // list. Held for a minimum 150ms so an instant cache hit doesn't flash.
  const showChatListSkeleton = useMinimumVisible(
    loading && listChats.length === 0 && !hasLoadedOnce
  );

  useEffect(() => {
    if (!markChatsReadData) return;
    unSelectAll();
    setIsEditing(false);
  }, [markChatsReadData]);

  const { checkClientIds } = useGetDocumentsByClientIdsQuery();

  const [refreshingArchived, setRefreshingArchived] = useState(false);

  const [hasNextPageArchived, setHasNextPageArchived] = useState(false);
  const [nextCursor, setCursor] = useState<string | null | undefined>(null);
  const [chatsArchived, setChatsData] = useState<Chat[]>([]);
  const refreshArchived = () => {
    setRefreshingArchived(true);
    refetchArchived().finally(() => setRefreshingArchived(false));
  };
  const afterComplete = (isNew: boolean, data: ListChatsPaginationQuery) => {
    const edges = data.listChatsPagination.edges;
    if (edges) {
      const newEdges = edges.filter((edge) => edge?.node);
      const newChats = newEdges?.map((edge) => edge?.node as Chat);

      if (isNew) {
        setChatsData(newChats);
      } else {
        const uniqueChats = [
          ...chatsArchived.filter(
            (chat) => !newChats?.some((newChat) => newChat?.id === chat.id)
          ),
          ...newChats,
        ];
        setChatsData(uniqueChats);
      }
    }
    const pageInfo = data.listChatsPagination.pageInfo;
    if (pageInfo.hasNextPage) {
      setHasNextPageArchived(pageInfo.hasNextPage);
      setCursor(pageInfo.endCursor);
    } else {
      setCursor(null);
    }
  };

  const {
    fetchMore: loadMoreArchivedx,
    loading: loadingArchived,
    refetch: refetchArchived,
    data: chatsArchivedData1,
  } = useListChatsPaginationQuery({
    variables: {
      archivesOnly: true,
      first: 20,
    },
    fetchPolicy: 'cache-and-network',
  });

  // TA-10279: same first-load skeleton rule as the main list, scoped to the
  // archived list's own `loadingArchived`/`chatsArchived` (previously the
  // empty-state gate below incorrectly checked the non-archived flags here).
  const showArchivedListSkeleton = useMinimumVisible(
    loadingArchived && chatsArchived.length === 0
  );

  useEffect(() => {
    isNewChat && setIsNewChatOpen(true);
  }, [isNewChat]);

  useEffect(() => {
    const storeAvatarUrls = async () => {
      const { AvatarThumbnailCachePrefix } =
        await import('@src/constants/constants');
      const chatsWithAvatarUrl = listChats.filter((t) => t.avatarUrl);

      if (chatsWithAvatarUrl.length > 0) {
        const entries = chatsWithAvatarUrl.map<[string, string]>((chat) => [
          AvatarThumbnailCachePrefix + chat.id,
          chat.avatarUrl!,
        ]);
        await AsyncStorage.multiSet(entries);
      }
    };
    storeAvatarUrls();
  }, [listChats]);

  useEffect(() => {
    if (chatsArchivedData1) afterComplete(true, chatsArchivedData1);
  }, [chatsArchivedData1]);
  const loadMoreArchived = () => {
    if (nextCursor && hasNextPageArchived) {
      loadMoreArchivedx({
        variables: {
          archivesOnly: true,
          after: nextCursor,
          first: 20,
        },
      }).then((res) => {
        afterComplete(false, res?.data);
      });
    }
  };

  const [filteredChats, setFilteredChats] = useState<Chat[]>([]);

  useEffect(() => {
    if (!chatListOpen) {
      setIsEditing(false);
      setIsSearching(false);
    }
    if (!isEditing) {
      unSelectAll();
    }
  }, [chatListOpen, isEditing]);

  // Timer used to refresh the render to update the chat message timestamps
  const [, setTimer] = useState(new Date());

  useEffect(() => {
    const interval = setInterval(() => {
      setTimer(new Date());
    }, 60000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const onFocus = () => {
      Sentry.addBreadcrumb({
        category: 'chat-list.focus',
        message: 'window.focus fired — refreshing listChatsPagination',
        level: 'info',
      });
      refresh();
    };
    window.addEventListener('focus', onFocus);
    // Calls refresh when the window first loads
    refresh();
    return () => {
      window.removeEventListener('focus', onFocus);
    };
  }, []);

  const menuList: MenuItem[] = [
    {
      iconName: 'Edit',
      name: t('shared:edit'),
      numberOfLines: 1,
      onSelect: () => setIsEditing((isEditing) => !isEditing),
    },
    {
      iconName: 'Archive',
      name: t('shared:archivedChat'),
      numberOfLines: 1,
      onSelect: () => setIsArchiveList(true),
    },
  ];

  const [search, setSearch] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [isSearchChanging, setIsSearchChanging] = useState(false);
  const PAGESIZE = 20;
  const [counter, setCounter] = useState(0);
  const [showFooter, setShowFooter] = useState(false);

  const [
    globalSearch,
    { data, loading: loadingSearchChats, error: searchError },
  ] = useSearchLazyQuery({
    fetchPolicy: 'no-cache',
  });

  const [searchedMessageList, setSearchedMessageList] = useState<
    Search<SearchableResult>[]
  >([]);
  const [searchedChatList, setSearchedChatList] = useState<Search<Chat>[]>([]);
  useEffect(() => {
    const searchData = (data?.search as Search<SearchableResult>[]) || [];
    const initialResults = {
      messages: [],
      tasks: [],
      documents: [],
      projects: [],
      contacts: [],
      chats: [],
    };

    // The response mixes Chat and Message results (TA-11072); dedupe within
    // each type only, since a Chat and a Message can share an id.
    const results =
      searchData.reduce<SearchResultType>((previous, result) => {
        if (isChatSearchResult(result))
          return {
            ...previous,
            chats: appendChatResults(previous.chats ?? [], [result]),
          };
        if (
          previous.messages.some(
            (existingResult) => existingResult.record.id === result.record.id
          )
        )
          return previous;
        if (isMessageSearchResult(result))
          return { ...previous, messages: [...previous.messages, result] };

        return previous;
      }, initialResults) || initialResults;

    if (search !== '') {
      setSearchedMessageList((prev) => [...prev, ...results.messages]);
      setSearchedChatList((prev) =>
        appendChatResults(prev, results.chats ?? [])
      );
      setCounter((c) => c + 1);
    }
    getLoadMore(results);
    // Per-type totals: Messages and Chats page independently, and a later
    // page can hold only one of them, so keep each type's last known total
    // (reset in handleSearch) instead of overwriting it with undefined.
    if (results.messages.length > 0) setChatTotal(results.messages[0].total);
    if (results.chats?.length) setChatResultsTotal(results.chats[0].total);
  }, [data]);

  useEffect(() => {
    if (searchError && searchedMessageList.length === 0) {
      setChatTotal(0);
      setChatResultsTotal(0);
      setCounter(0);
      setShowFooter(false);
      setChatLength(0);
    }
  }, [searchError, searchedMessageList]);

  const getLoadMore = (results: SearchResultType) => {
    if (
      results.messages.length > 0 &&
      results.messages.length < results.messages[0].total
    ) {
      setChatLength(results.messages.length);
    } else {
      setChatLength(0);
    }
  };
  const [_chatTotal, setChatTotal] = useState(0);
  const [chatResultsTotal, setChatResultsTotal] = useState(0);
  // Either type may still have pages left; the backend applies the same
  // `from` to both, so one request advances whichever has more (TA-11072).
  const hasMoreSearchResults =
    _chatTotal > searchedMessageList.length ||
    chatResultsTotal > searchedChatList.length;
  // A short first page of both types means there is nothing to load more of.
  const hasFullSearchPage =
    searchedMessageList.length >= PAGESIZE ||
    searchedChatList.length >= PAGESIZE;
  const globalSearchCall = () => {
    const from = PAGESIZE * counter;
    const total = Math.max(_chatTotal || 0, chatResultsTotal);
    if (!search || from > total) return;
    if (search) {
      // Trim to match the term useDebouncedSearch already sent for page 0 —
      // using the raw (untrimmed) search state here could send a different
      // term than page 0 for input with leading/trailing whitespace,
      // breaking pagination consistency (TA-10168).
      return globalSearch({
        variables: {
          term: search.trim(),
          size: PAGESIZE,
          from: from,
          includeMessages: true,
          includeChats: true,
          includeContacts: false,
          includeDocuments: false,
          includeProjects: false,
          includeTasks: false,
          tags: filter.includes('Tags'),
          files: filter.includes('Files'),
          includeActivities: false,
          media: false,
        },
      });
    }
  };
  const renderLoadMoreComponent = (): JSX.Element => {
    return loadingSearchChats ? (
      <Box alignItems='center' minHeight={40} p='s' minWidth={100}>
        <ActivityIndicatorLoading />
      </Box>
    ) : (
      <Box alignItems='center'>
        {hasMoreSearchResults && (
          <TouchableOpacity
            // Disabled while the term is still debouncing (isPending) —
            // otherwise globalSearchCall would query the NEW live term with
            // an offset (counter) still accumulated against the OLD
            // settled term, mixing mismatched results at a nonsensical
            // pagination offset (TA-10168).
            disabled={loadingSearchChats || isPending}
            accessibilityLabel={t('shared:loadMore')}
            onPress={() => !isPending && globalSearchCall()}>
            <Box
              marginVertical='s'
              backgroundColor='grey01'
              p='s'
              borderRadius='xs'
              alignSelf='flex-start'
              minWidth={100}
              minHeight={40}>
              <Text variant='labelSmall' textAlign='center'>
                {t('shared:loadMore')}
              </Text>
            </Box>
          </TouchableOpacity>
        )}
      </Box>
    );
  };

  const [_isChatLength, setChatLength] = useState(0);

  const {
    filter,
    setIsSearching: setChatDetailIsSearching,
    setIsSearchPreview,
  } = useSearch();

  // Single settled/gated callback drives the initial (from=0) message
  // search — calls globalSearch directly (not via globalSearchCall, which
  // reads `counter` for pagination) since setCounter(0) below doesn't apply
  // until the next render, so globalSearchCall would still read the stale
  // pre-reset counter if called synchronously here. The "load more" footer
  // button still calls globalSearchCall directly, bypassing the
  // debounce/threshold (TA-10168).
  const handleSearch = useCallback(
    (term: string) => {
      setCounter(0);
      setSearchedMessageList([]);
      setSearchedChatList([]);
      setChatLength(0);
      setChatTotal(0);
      setChatResultsTotal(0);
      return globalSearch({
        variables: {
          term,
          size: PAGESIZE,
          from: 0,
          includeMessages: true,
          includeChats: true,
          includeContacts: false,
          includeDocuments: false,
          includeProjects: false,
          includeTasks: false,
          tags: filter.includes('Tags'),
          files: filter.includes('Files'),
          includeActivities: false,
          media: false,
        },
      });
    },
    [globalSearch, filter]
  );

  const { isPending, isBelowThreshold, hasSearched } = useDebouncedSearch(
    search,
    handleSearch
  );
  // Gated on !isBelowThreshold (not just OR'd with loadingSearchChats) so a
  // still-in-flight request from before the term dropped below threshold
  // can't leak through and show a spinner instead of the "type more" prompt.
  const isSearchLoading =
    !isBelowThreshold && (loadingSearchChats || isPending || !hasSearched);

  useEffect(() => {
    if (search) setIsSearchChanging(true);
  }, [search]);

  useEffect(() => {
    if (loadingSearchChats) setIsSearchChanging(false);
  }, [loadingSearchChats]);

  // GlobalSearchList renders whatever is in searchedMessageList regardless
  // of the "type more" header it shows below threshold, so stale results
  // from an earlier above-threshold search must be cleared explicitly here
  // (TA-10168). Also resets isSearchChanging — below threshold no query
  // ever fires, so loadingSearchChats never flips true to reset it there,
  // which would otherwise leave it stuck true and permanently suppress
  // hasError for as long as the term stays below threshold.
  useEffect(() => {
    if (isBelowThreshold) {
      setSearchedMessageList([]);
      setSearchedChatList([]);
      setCounter(0);
      setChatLength(0);
      setChatTotal(0);
      setChatResultsTotal(0);
      setIsSearchChanging(false);
    }
  }, [isBelowThreshold]);

  useEffect(() => {
    setShowFooter(hasFullSearchPage);

    const list: string[] = searchedMessageList
      .flatMap((m) => m.record?.attachments ?? [])
      .filter((att): att is Document => att?.__typename === 'Document')
      .map((t) => t.clientId);

    if (list.length > 0) {
      checkClientIds(list);
    }
  }, [hasFullSearchPage, searchedMessageList]);

  useEffect(() => {
    if (isArchiveList) {
      search
        ? setFilteredChats(
            chatsArchived.filter((chat) =>
              chat.name
                .trim()
                .toLowerCase()
                .includes(search.trim().toLowerCase())
            ) as Chat[]
          )
        : setFilteredChats(chatsArchived as Chat[]);
    }
  }, [search]);

  const searchInputRef = useRef<TextInput>(null);

  const handleToggleOpen = () => {
    toggleChatListOpen();
  };

  const openChat = (chat) => {
    showMessageEmojiModal && setShowMessageEmojiModal(false);
    setIsNewChatOpen(false);
    isNewChat && setIsNewChat(false);
    if (isEditing) {
      toggleSelected(chat);
    } else {
      if (!chatExpanded && chatListOpen) {
        setShouldChatListOpen(true);
        setChatListOpen(false);
      }
      setIsOpenChat(true);
      setChatDetailIsSearching(false);

      setSearch('');
      setIsSearching(false);
      setIsSearchPreview(false);
      setActiveMessageId(undefined);
      navigate.navigateToChatDrawer({
        screen: ChatDrawerScreen.details,
        activeChat: chat,
      });
    }
  };

  // TA-10892 #33: once Task Creation onboarding is complete, one
  // invited-and-assigned member → open (or create) their chat; two or more →
  // keep the list and highlight their chats. Re-runs as the list loads, since
  // a new invitee's chat may land in a later refresh.
  const { me } = useMe();
  const onboardingActive = useOnboardingActive();
  const firstChatSent = me?.onboardingState?.firstChatSent ?? false;
  const taskCreationComplete =
    me?.onboardingState?.taskCreationComplete ?? false;
  const [invitedChatIds, setInvitedChatIds] = useState<Set<string>>(
    () => new Set()
  );
  const autoOpenedInviteeRef = useRef(false);
  const [createInviteeChat] = useCreateChatMutation();
  const [inviteesVersion, setInviteesVersion] = useState(0);
  useEffect(() => {
    const listener = DeviceEventEmitter.addListener(
      ONBOARDING_INVITEES_UPDATED,
      () => setInviteesVersion((v) => v + 1)
    );
    return () => listener.remove();
  }, []);
  useEffect(() => {
    if (!onboardingActive) return;
    if (readOnboardingInvitees().length === 0) return;
    if (firstChatSent) {
      // The Owner has started chatting — the invitation cue has done its job.
      clearOnboardingInvitees();
      setInvitedChatIds(new Set());
      return;
    }
    // Recorded at project creation, acted on only once the first task has
    // been created and assigned — and only for members who were both
    // invited and assigned.
    if (!taskCreationComplete) return;
    const invitees = getAssignedOnboardingInvitees();
    if (invitees.length === 0) return;
    const chats = findInviteeChats(listChats, invitees, me?.id);
    if (invitees.length === 1) {
      if (autoOpenedInviteeRef.current) return;
      if (chats.length === 1) {
        autoOpenedInviteeRef.current = true;
        clearOnboardingInvitees();
        openChat(chats[0]);
        return;
      }
      // Not in the list once it has settled: create the 1:1 chat and open
      // it. Only for a single invitee — with two or more nothing is created.
      if (!hasLoadedOnce || loading || hasNextPage) return;
      const [invitee] = invitees;
      autoOpenedInviteeRef.current = true;
      clearOnboardingInvitees();
      createInviteeChat({
        variables: {
          attributes: invitee.id
            ? { name: '', userIds: [invitee.id] }
            : { name: invitee.email ?? '', emails: [invitee.email ?? ''] },
        },
      })
        .then(({ data }) => {
          if (data?.createChat?.id) openChat(data.createChat);
        })
        .catch(() => {
          // Leave the chat list as is; the Owner can start the chat manually.
        });
      return;
    }
    setInvitedChatIds((prev) => {
      const next = new Set(chats.map((c) => c.id));
      return next.size === prev.size && [...next].every((id) => prev.has(id))
        ? prev
        : next;
    });
  }, [
    listChats,
    onboardingActive,
    firstChatSent,
    taskCreationComplete,
    me?.id,
    hasLoadedOnce,
    loading,
    hasNextPage,
    inviteesVersion,
  ]);

  // The rows the list actually renders: the archived list when that is open,
  // otherwise the main list, narrowed to unread chats while editing. Memoised
  // so every consumer in a render sees the same array — it was previously a
  // function called a dozen times per render, allocating a fresh filtered
  // array each time.
  const displayedChats = useMemo(() => {
    const list = isArchiveList ? chatsArchived : listChats;
    if (!isEditing) return list;
    return list.filter((t) => (t.unreadCount ?? 0) > 0);
  }, [isArchiveList, chatsArchived, listChats, isEditing]);

  // TA-10627: compare by id, not by count. `selected` can hold the same
  // NUMBER of chats as the visible list while holding a different SET — the
  // unread filter shifts underneath edit mode whenever a chatUpdate lands or
  // a chat is read elsewhere. Comparing lengths made the radio read
  // "selected" with visible rows still unselected, and made the next press
  // clear the selection instead of selecting those rows.
  const selectedIds = new Set(selected.map((chat) => chat.id));
  const areAllDisplayedSelected =
    displayedChats.length > 0 &&
    displayedChats.every((chat) => selectedIds.has(chat.id));

  // Drives Select All directly rather than leaning on useSelected.selectAll's
  // own length-based toggle, which has the same same-count/different-set hole:
  // it treats a selection that merely MATCHES the incoming length as "already
  // all selected" and clears instead. Clearing first makes the follow-up
  // selectAll unambiguous — useSelected's updaters are functional, so the
  // second one reads the emptied list rather than this render's snapshot.
  const handleToggleSelectAll = () => {
    unSelectAll();
    if (!areAllDisplayedSelected) selectAll(displayedChats);
  };

  // TA-10627: mark read exactly what is both selected AND on screen. The
  // selection drifts out of the visible set on its own — a chat read
  // elsewhere leaves the unread filter while staying selected — and sending
  // an id for a row the user cannot see is what produced the blank rows in
  // the first place. Filtering here holds the invariant at the real boundary
  // no matter how the selection got that way, and matches list order.
  const readSelected = () => {
    const selectedChatIds = displayedChats
      .filter((chat) => selectedIds.has(chat.id))
      .map((chat) => chat.id);
    if (!selectedChatIds.length) return;
    markChatsRead({
      variables: {
        attributes: {
          chatIds: selectedChatIds,
        },
      },
    });
  };

  const chatWidth = Dimensions.get('window').width <= 1280 ? '480px' : '550px';

  const getTitle = () => {
    if (chatListOpen) {
      return isSearching ? 'Chat Search' : 'Chat';
    }
    return '';
  };

  const getOnCloseHandler = () => {
    if (isSearching) {
      return () => setIsSearching(false);
    }
    if (isEditing) {
      return () => setIsEditing(false);
    }
    if (isArchiveList) {
      return () => setIsArchiveList(false);
    }
    return undefined;
  };

  const getChatList = () => {
    if (chatListOpen && isEditing && displayedChats.length == 0) return false;

    // TA-10279: 3 skeleton rows on mount, held for a minimum 150ms — never a
    // blank region or a premature empty state while the first fetch is in
    // flight.
    if (isArchiveList ? showArchivedListSkeleton : showChatListSkeleton) {
      return (
        <SkeletonList
          count={3}
          row={() => <ChatRowSkeleton collapsed={!chatListOpen} />}
        />
      );
    }

    return (
      <FadeInView style={{ flex: 1 }}>
        <ChatList
          isArchiveList={isArchiveList}
          fetchChats={isArchiveList ? refetchArchived : fetchChats}
          open={chatListOpen}
          loading={isArchiveList ? loadingArchived : loading}
          isEditing={isEditing}
          chats={displayedChats}
          selected={selected}
          invitedChatIds={isArchiveList ? undefined : invitedChatIds}
          onPress={async (chat) => {
            {
              await AsyncStorage.setItem('message', ''); // clear local message
              openChat(chat);
              isFromChatList &&
                chatTutorialWebState === '4' &&
                setChatTutorialWebStateAndInStorage('5');
            }
          }}
          refreshing={isArchiveList ? refreshingArchived : refreshing}
          onRefresh={isArchiveList ? refreshArchived : refresh}
          hideSeparatorItem={true}
          hasNextPage={isArchiveList ? hasNextPageArchived : hasNextPage}
          loadMore={isArchiveList ? loadMoreArchived : loadMore}
        />
      </FadeInView>
    );
  };

  // Full-screen error fallback — only when the query has never succeeded.
  // If we've had at least one successful load (even with 0 chats), keep the
  // current view visible and show an inline banner instead (below), so users
  // with an empty chat list don't lose their empty state on a network drop.
  if (hasError && !hasLoadedOnce) {
    return (
      <Box
        accessibilityLabel='Chats modal'
        width={chatListOpen ? chatWidth : '81px'}
        height='100vh'
        maxHeight='100vh'
        flexDirection='column'
        justifyContent='center'
        alignItems='center'
        padding='l'>
        <Text variant='headerLabel' textAlign='center' marginBottom='m'>
          {t('models:chat.errorState.title')}
        </Text>
        <Text variant='bodySecondary' textAlign='center' marginBottom='l'>
          {t('models:chat.errorState.message')}
        </Text>
        <Button
          variant='primary'
          onPress={resetError}
          accessibilityLabel={t('models:chat.errorState.retry')}>
          {t('models:chat.errorState.retry')}
        </Button>
      </Box>
    );
  }

  return (
    <Box
      accessibilityLabel='Chats modal'
      width={chatListOpen ? chatWidth : '81px'}
      height='100vh'
      maxHeight='100vh'
      flexDirection='column'
      justifyContent='flex-end'>
      <Drawer
        title={getTitle()}
        titleVariant='heading2'
        onOpenToggle={handleToggleOpen}
        onClose={getOnCloseHandler()}
        onSearch={() => {
          if (!isSearching) {
            setSearch('');
            setSearchedMessageList([]);
            setSearchedChatList([]);
          }
          setIsSearching(!isSearching);
        }}
        open={chatListOpen}
        elevated={!currentScreen}
        menuList={(!isEditing && !isArchiveList && menuList) || undefined}>
        {chatListOpen && !isEditing && isSearching && !isArchiveList && (
          <Box mx='m'>
            <SearchInput
              autoFocus
              ref={searchInputRef}
              value={search}
              onFocus={() => {
                setIsSearching(true);
              }}
              onBlur={() => {
                if (!search) {
                  setSearch('');
                }
              }}
              onTextChanged={(t: string) => {
                setSearch(t);
              }}
              onCancel={() => {
                setSearch('');
                setSearchedMessageList([]);
                setSearchedChatList([]);
              }}
            />
            <Box mt='m'>
              <ScrollView
                style={{
                  maxHeight: 'calc(100vh - 180px)',
                  minHeight: '100px',
                }}
                showsVerticalScrollIndicator={false}>
                <ChatsSearch
                  search={search}
                  data={searchedMessageList}
                  chats={searchedChatList}
                  loading={
                    isSearchLoading &&
                    searchedMessageList.length === 0 &&
                    searchedChatList.length === 0
                  }
                  sectionFooter={renderLoadMoreComponent()}
                  showFooter={showFooter && hasFullSearchPage}
                  hasError={
                    !!searchError && !loadingSearchChats && !isSearchChanging
                  }
                  onPress={() => {
                    setSearch('');
                    setIsSearching(false);
                    setIsNewChatOpen(false);
                    if (!chatExpanded && chatListOpen) {
                      setShouldChatListOpen(true);
                      setChatListOpen(false);
                    }
                    setIsOpenChat(true);
                  }}
                />
              </ScrollView>
            </Box>
          </Box>
        )}

        {chatListOpen && isSearching && isArchiveList && (
          <Box flexDirection='column' flex={1} mb='m'>
            <Box flex={1}>
              {showArchivedListSkeleton ? (
                <SkeletonList count={3} row={() => <ChatRowSkeleton />} />
              ) : (
                <FadeInView style={{ flex: 1 }}>
                  <ChatList
                    fetchChats={refetchArchived}
                    open={chatListOpen}
                    loading={loadingArchived}
                    isEditing={isEditing}
                    chats={filteredChats ?? chatsArchived}
                    selected={selected}
                    filterVal={search}
                    hideSeparatorItem={true}
                    onPress={(chat) => {
                      openChat(chat);
                    }}
                    refreshing={refreshingArchived}
                    onRefresh={refreshArchived}
                    hasNextPage={hasNextPageArchived}
                    loadMore={loadMoreArchived}
                  />
                </FadeInView>
              )}
            </Box>
          </Box>
        )}

        {!isSearching && (
          <Box flexDirection='column' flex={1}>
            {hasError && hasLoadedOnce && chatListOpen && !isArchiveList && (
              <Box
                flexDirection='row'
                alignItems='center'
                backgroundColor='alertRedMild'
                paddingHorizontal='m'
                paddingVertical='s'
                mx='m'
                mb='s'
                borderRadius='xs'>
                <Box flex={1}>
                  <Text variant='labelSmall' color='alertRed'>
                    {t('models:chat.errorState.bannerMessage')}
                  </Text>
                </Box>
                <TouchableOpacity
                  onPress={resetError}
                  accessibilityLabel={t('models:chat.errorState.retry')}>
                  <Text
                    variant='labelSmall'
                    color='alertRed'
                    style={{ textDecorationLine: 'underline' }}>
                    {t('models:chat.errorState.retry')}
                  </Text>
                </TouchableOpacity>
              </Box>
            )}
            <Box mx='m'>
              {/* TA-10627: Select All must operate on the rows edit mode
                  actually shows (`displayedChats` — unread only, and the
                  archived list while that is open), not the full `listChats`.
                  Selecting off-screen chats sent their ids to markChatsRead,
                  and the chatUpdate events that came back bumped chats which
                  were never in the cached page to the top of the list as
                  blank rows. Matches mobile ChatSelect.tsx, which already
                  selects only its filtered unread list. */}
              {chatListOpen && isEditing && displayedChats.length > 0 && (
                <TouchableOpacity
                  onPress={handleToggleSelectAll}
                  style={{ alignSelf: 'flex-start' }}>
                  <Box
                    flexDirection='row'
                    marginVertical='s'
                    alignSelf='flex-start'>
                    <RadioButton
                      onPress={handleToggleSelectAll}
                      isSelected={areAllDisplayedSelected}
                    />
                    <Text variant='labelEmphasized' marginLeft='m'>
                      Select All
                    </Text>
                  </Box>
                </TouchableOpacity>
              )}
            </Box>
            {/* TA-10279: previously always checked the non-archived `loading`/
                `listChats`, even while viewing the archived list, and could
                render this empty state while the first fetch was still in
                flight (only `loading`-gated, not skeleton-hold-gated). Now
                gated on the same skeleton-hold flag the list itself uses.
                Still scoped to `!isArchiveList` — this "Start a new chat"
                CTA is the main-list empty state; an archived list with zero
                archived chats is normal, not an error/empty state to react
                to the same way. */}
            {!isArchiveList &&
              !showChatListSkeleton &&
              listChats.length == 0 &&
              chatListOpen && (
                <Box flex={1}>
                  {isQRshow && (
                    <ChatEmptyState
                      onClose={() => {
                        setIsQRshow(false);
                        setIsBottomBannerShow(true);
                      }}
                      type={EmptyStateType.Chat}></ChatEmptyState>
                  )}
                  {!isQRshow && (
                    <Box
                      flex={1}
                      alignItems='center'
                      px='listFooter'
                      pt='listFooter'>
                      <EmptyChatList />
                      <Box mb='xs' width={155}>
                        <Button
                          backgroundColor='textPrimary'
                          onPress={() => {
                            setActiveMessageId(undefined);
                            setIsNewChatOpen(true);
                          }}
                          borderRadius='xl'
                          variant='primary'
                          isSmall
                          accessibilityLabel={t(
                            'models:chat.labels.startANewChat'
                          )}>
                          {t('models:chat.labels.startANewChat')}
                        </Button>
                      </Box>
                    </Box>
                  )}
                </Box>
              )}
            <Box flex={1}>
              {chatListOpen && isEditing && displayedChats.length == 0 && (
                <Box flex={1} paddingTop='m' alignItems='center'>
                  <Text>{t('models:chat.labels.unreadChatNotFound')}</Text>
                </Box>
              )}

              {isFromChatList &&
              chatTutorialWebState === '4' &&
              chatHeaderHeight > 50 ? (
                <ChatOverlay
                  visible={
                    isFromChatList &&
                    chatTutorialWebState === '4' &&
                    chatHeaderHeight > 50
                  }
                  children={
                    <Box
                      style={{
                        position: 'absolute',
                        top: chatHeaderHeight,
                        width: '100%',
                        height: chatListOpen
                          ? 290 - (displayedChats.length < 2 ? 68 : 0)
                          : 68 *
                              (displayedChats.length < 3
                                ? displayedChats.length
                                : 3) +
                            10,
                        borderRadius: theme.spacing.xs,
                        backgroundColor: theme.colors.white,
                        padding: theme.spacing.xs,
                      }}>
                      {getChatList()}
                      <Box
                        position='absolute'
                        top={
                          68 *
                            (displayedChats.length < 3
                              ? displayedChats.length
                              : 2) +
                          10
                        }
                        left={76}
                        width={313}>
                        <TooltipPopup
                          animate={true}
                          visible={
                            isFromChatList &&
                            chatListOpen &&
                            chatTutorialWebState === '4' &&
                            chatHeaderHeight > 50
                          }
                          title='Continue Conversation'
                          description='Select the chat from the list to open it.'
                          pointerPosition='top-left'
                          currentStep={1}
                          totalSteps={4}
                        />
                      </Box>
                      <Box
                        position='absolute'
                        top={
                          (75 + 68 * 2 - tooltipPopupHeight) / 2 -
                          (displayedChats.length === 1 ? 68 : 0) +
                          5
                        }
                        right={81 + 5}
                        width={313}>
                        <TooltipPopup
                          animate={true}
                          visible={
                            !chatListOpen &&
                            !isOpenChat &&
                            isFromChatList &&
                            chatTutorialWebState === '4'
                          }
                          title='Continue Conversation'
                          description='Select the chat from the list to open it.'
                          pointerPosition='right-center'
                          currentStep={1}
                          totalSteps={4}
                        />
                      </Box>
                    </Box>
                  }
                />
              ) : (
                getChatList()
              )}

              {!isEditing &&
                !isArchiveList &&
                chatListOpen &&
                listChats.length > 0 && (
                  <Box mr='xxs'>
                    <Button
                      backgroundColor='textPrimary'
                      prefix={
                        <Box ml='s' mr='xxs'>
                          <Icon variant='l' name='ChatNew' color='white' />
                        </Box>
                      }
                      onPress={() => {
                        setActiveMessageId(undefined);
                        setIsNewChatOpen(true);
                      }}
                      borderRadius='xl'
                      variant='primary'
                      prefixMarginRight='s'
                      float='bottom-right'
                      isSmall
                      accessibilityLabel={t('models:chat.labels.newMessage')}>
                      {t('models:chat.labels.newMessage')}
                    </Button>
                  </Box>
                )}

              {!isEditing && !isArchiveList && !chatListOpen && (
                <Box
                  position={
                    isFromChatList &&
                    chatTutorialWebState === '4' &&
                    chatHeaderHeight > 50
                      ? 'absolute'
                      : undefined
                  }
                  bottom={
                    isFromChatList &&
                    chatTutorialWebState === '4' &&
                    chatHeaderHeight > 50
                      ? 0
                      : undefined
                  }
                  right={
                    isFromChatList &&
                    chatTutorialWebState === '4' &&
                    chatHeaderHeight > 50
                      ? theme.spacing.m
                      : undefined
                  }
                  mt='xs'
                  mb='m'
                  backgroundColor='background'
                  justifyContent='center'
                  alignItems='center'>
                  <TouchableOpacity
                    onPress={() => {
                      setActiveMessageId(undefined);
                      setIsNewChatOpen(true);
                    }}>
                    <Box
                      width={48}
                      height={48}
                      bg='textPrimary'
                      borderRadius='l'
                      justifyContent='center'
                      alignItems='center'>
                      <Icon variant='l' name='ChatNew' color='white' />
                    </Box>
                  </TouchableOpacity>
                </Box>
              )}
            </Box>
          </Box>
        )}
        {chatListOpen && isEditing && displayedChats.length > 0 && (
          <TouchableOpacity>
            <Button
              marginHorizontal='s'
              disabled={!selected.length}
              variant='primary'
              onPress={selected.length > 0 ? readSelected : undefined}
              float='bottom-inset'
              accessibilityLabel={t('models:chat.edit.read')}>
              {t('models:chat.edit.read')}
            </Button>
          </TouchableOpacity>
        )}
        {isNewChatOpen && (
          <NewChatModal
            showModal={isNewChatOpen}
            onClose={() => {
              setIsNewChatOpen(false);
              isNewChat && setIsNewChat(false);
            }}
            onOpenChat={async (chat) => {
              await AsyncStorage.setItem('message', ''); // clear local message
              openChat(chat);
              if (isVisibleForChat) {
                setShowOverlayForChat(true);
                chatTutorialWebState === '0' &&
                  setChatTutorialWebStateAndInStorage('1');
              }
            }}
          />
        )}
      </Drawer>
    </Box>
  );
};
